<!--
	@component
	Generates a tooltip that works on multiseries datasets, like multiline charts. It creates a tooltip showing the name of the series and the current value. This version uses percentages so you can use it to render server-side. It finds the nearest data point using the [QuadTree.percent-range.html.svelte](https://layercake.graphics/components/QuadTree.percent-range.html.svelte) component.
 -->
<script>
	import { getContext } from "svelte";
	import { format } from "d3-format";
	import { fade, blur, fly, slide, scale } from "svelte/transition";
	import { beforeUpdate, afterUpdate } from "svelte";

	import QuadTree from "./QuadTree.svelte";
	import { percent } from "../utils";

	const { data, width, yScale, config, yGet, y } = getContext("LayerCake");

	const commas = format(",");
	const titleCase = (d) => d.replace(/^\w/, (w) => w.toUpperCase());

	/** @type {Function} [formatTitle=d => d] – A function to format the tooltip title, which is `$config.x`. */
	export let formatTitle = (d) => d;

	/** @type {Function} [formatKey=d => titleCase(d)] – A function to format the series name. */
	export let formatKey = (d) => titleCase(d);

	/** @type {Function} [formatValue=d => isNaN(+d) ? d : commas(d)] – A function to format the value. */
	export let formatValue = (d) => (isNaN(+d) ? d : commas(d));

	/** @type {Number} [offset=-20] – A y-offset from the hover point, in pixels. */
	export let offset = -20;

	/** @type {Array} [dataset] – The dataset to work off of—defaults to $data if left unset. You can pass something custom in here in case you don't want to use the main data or it's in a strange format. */
	export let dataset = undefined;

	export let shownKeys = dataset ? Object.keys(dataset[0]) : Object.keys($data[0]);

	export let range = [];
	export let color = "#666";
	export let lineColor = color;

	const w = 150;
	const w2 = w / 2;
	let top = 0;

	/* --------------------------------------------
	 * Sort the keys by the highest value
	 */
	function sortResult(result) {
		if (Object.keys(result).length === 0) return [];
		const rows = Object.keys(result)
			.filter((d) => d !== $config.x)
			.map((key) => {
				return {
					key,
					value: result[key],
					data: result,
				};
			})
			.sort((a, b) => b.value - a.value);

		return rows;
	}

	const getRange = (d) => range.filter((r) => +r.date === +d.date)[0];
</script>

<QuadTree dataset={dataset || $data} y="x" let:x let:y let:visible let:found let:e>
	{@const foundSorted = sortResult(found).filter((d) => shownKeys.includes(d.key))}
	{#if visible === true && Object.keys(found).length !== 0}
		{@const y = $yGet(found)}
		{@const range = getRange(found)}
		{@const yMin = $yScale(range.min)}
		{@const yMax = $yScale(range.max)}
		{@const height = yMin - yMax}
		<div class="tooltip-container" class:right={x > 80} class:left={x < 20} class:top={y < 20}>
			<div
				style:left={percent(x)}
				style:top={percent(yMax)}
				style:height={percent(height)}
				class="line"
				style:border-color={lineColor}
				style:background-color={lineColor}
				transition:fade
			/>
			<div
				class="circle"
				style:background-color={color}
				style:left={percent(x)}
				style:top={percent(y)}
				transition:fade
			/>
			<div class="label" style:left={percent(x)} style:top={percent(y)} style:color transition:fade>
				<span>{formatTitle(found.date)}</span><br />
				<span>{formatValue(found.value)}</span>
			</div>
		</div>
	{/if}
</QuadTree>

<style lang="scss">
	.tooltip {
		position: absolute;
		font-size: 13px;
		pointer-events: none;
		border: 1px solid #ccc;
		background: rgba(255, 255, 255, 0.85);
		transform: translate(-50%, -100%);
		padding: 5px;
		z-index: 15;
		pointer-events: none;
	}

	.line {
		position: absolute;
		top: 0;
		bottom: 0;
		width: 2px;
		background-color: #666;
		pointer-events: none;
		transform: translateX(-1px);
	}

	.circle {
		position: absolute;
		border-radius: 50%;
		width: 10px;
		height: 10px;
		transform: translate(-50%, -50%);
		background-color: #999;
		outline: 2px solid #fff;
		box-shadow: inset 0px 0px 2px rgba(0, 0, 0, 0.25);
	}

	.label {
		position: absolute;
		transform: translate(10px, -120%);
		font-weight: 500;
		font-size: 11.5px;
		line-height: 11px;
		width: fit-content;
		text-shadow: 2px 0 0 #fff, -2px 0 0 #fff, 0 2px 0 #fff, 0 -2px 0 #fff, 1px 1px #fff,
			-1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff;
		pointer-events: none;
	}

	.right {
		.label {
			transform: translate(calc(-100% - 10px), -120%);
		}
	}

	.top {
		.label {
			transform: translate(10px, 0%);
		}

		&.right {
			.label {
				transform: translate(calc(-100% - 10px), 0%);
			}
		}
	}

	.title {
		font-weight: bold;
	}

	.key {
		color: #999;
	}
</style>
